import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Search,
  Plus,
  Trash2,
  X,
  Calendar,
  Clock,
  User,
  FileText,
  Bell,
  ChevronDown,
  ChevronUp,
  Upload,
  Download,
  AlertCircle,
  CheckCircle,
  Edit2,
  Save,
  ListTodo,
  Send,
  Repeat,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function JobDuties({ embedded = false }) {
  const [duties, setDuties] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedDuty, setSelectedDuty] = useState(null);
  const [expandedDuty, setExpandedDuty] = useState(null);
  const [stats, setStats] = useState(null);
  const fileInputRef = useRef(null);
  const reminderFileInputRef = useRef(null);
  const [uploadingFor, setUploadingFor] = useState(null);
  const [documentName, setDocumentName] = useState('');
  
  // New state for editing and modals
  const [editingDescription, setEditingDescription] = useState(null);
  const [editedDescription, setEditedDescription] = useState('');
  const [editingReminder, setEditingReminder] = useState(null);
  const [editedReminder, setEditedReminder] = useState({});
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [summaryData, setSummaryData] = useState([]);
  const [pendingReminders, setPendingReminders] = useState([]);

  const [newDuty, setNewDuty] = useState({
    title: '',
    description: '',
    assigned_to: '',
    priority: 'Normal',
    due_date: '',
  });

  const [newReminder, setNewReminder] = useState({
    reminder_date: '',
    reminder_time: '09:00',
    email_subject: '',
    email_body: '',
    is_recurring: false,
    recurrence_type: '',
    recurrence_interval: 1,
    recurrence_unit: 'days',
  });

  useEffect(() => {
    fetchDuties();
    fetchPersonnel();
    fetchStats();
  }, [statusFilter]);

  const fetchDuties = async () => {
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (search) params.search = search;
      const response = await axios.get(`${API}/admin/job-duties`, { params });
      setDuties(response.data);
    } catch (error) {
      toast.error('Failed to load job duties');
    } finally {
      setLoading(false);
    }
  };

  const fetchPersonnel = async () => {
    try {
      const response = await axios.get(`${API}/personnel?status=Active`);
      // Sort by last name, then first name
      const sorted = response.data.sort((a, b) => {
        const lastNameCompare = (a.last_name || '').localeCompare(b.last_name || '');
        if (lastNameCompare !== 0) return lastNameCompare;
        return (a.first_name || '').localeCompare(b.first_name || '');
      });
      setPersonnel(sorted);
    } catch (error) {
      console.error('Failed to load personnel:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/job-duties-stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const handleSearch = () => {
    fetchDuties();
  };

  const handleCreateDuty = async (e) => {
    e.preventDefault();
    if (!newDuty.title) {
      toast.error('Title is required');
      return;
    }
    try {
      await axios.post(`${API}/admin/job-duties`, {
        ...newDuty,
        assigned_to: newDuty.assigned_to || null,
      });
      toast.success('Job duty created');
      setShowAddModal(false);
      setNewDuty({
        title: '',
        description: '',
        assigned_to: '',
        priority: 'Normal',
        due_date: '',
      });
      fetchDuties();
      fetchStats();
    } catch (error) {
      toast.error('Failed to create job duty');
    }
  };

  const handleUpdateDuty = async (dutyId, updates) => {
    try {
      await axios.put(`${API}/admin/job-duties/${dutyId}`, updates);
      toast.success('Job duty updated');
      fetchDuties();
      fetchStats();
    } catch (error) {
      toast.error('Failed to update job duty');
    }
  };

  const handleDeleteDuty = async (dutyId) => {
    if (!window.confirm('Delete this job duty?')) return;
    try {
      await axios.delete(`${API}/admin/job-duties/${dutyId}`);
      toast.success('Job duty deleted');
      setExpandedDuty(null);
      fetchDuties();
      fetchStats();
    } catch (error) {
      toast.error('Failed to delete job duty');
    }
  };

  const handleAddReminder = async (dutyId) => {
    if (!newReminder.reminder_date) {
      toast.error('Reminder date is required');
      return;
    }
    try {
      const reminderData = {
        ...newReminder,
        is_recurring: newReminder.is_recurring,
        recurrence_type: newReminder.is_recurring ? newReminder.recurrence_type : null,
        recurrence_interval: newReminder.recurrence_type === 'custom' ? newReminder.recurrence_interval : null,
        recurrence_unit: newReminder.recurrence_type === 'custom' ? newReminder.recurrence_unit : null,
      };
      await axios.post(`${API}/admin/job-duties/${dutyId}/reminders`, reminderData);
      toast.success('Reminder added');
      setNewReminder({
        reminder_date: '',
        reminder_time: '09:00',
        email_subject: '',
        email_body: '',
        is_recurring: false,
        recurrence_type: '',
        recurrence_interval: 1,
        recurrence_unit: 'days',
      });
      fetchDuties();
      fetchStats();
    } catch (error) {
      toast.error('Failed to add reminder');
    }
  };

  const handleUpdateReminder = async (dutyId, reminderId) => {
    try {
      await axios.put(`${API}/admin/job-duties/${dutyId}/reminders/${reminderId}`, editedReminder);
      toast.success('Reminder updated');
      setEditingReminder(null);
      setEditedReminder({});
      fetchDuties();
    } catch (error) {
      toast.error('Failed to update reminder');
    }
  };

  const handleCompleteReminder = async (dutyId, reminderId) => {
    try {
      await axios.put(`${API}/admin/job-duties/${dutyId}/reminders/${reminderId}/complete`);
      toast.success('Reminder marked as complete');
      fetchDuties();
      fetchStats();
      fetchPendingReminders();
    } catch (error) {
      toast.error('Failed to complete reminder');
    }
  };

  const handleSendReminder = async (dutyId, reminderId) => {
    try {
      const response = await axios.post(`${API}/admin/job-duties/${dutyId}/reminders/${reminderId}/send`);
      toast.success(response.data.message);
      fetchPendingReminders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reminder');
    }
  };

  const handleSaveDescription = async (dutyId) => {
    try {
      await axios.put(`${API}/admin/job-duties/${dutyId}`, { description: editedDescription });
      toast.success('Description updated');
      setEditingDescription(null);
      setEditedDescription('');
      fetchDuties();
    } catch (error) {
      toast.error('Failed to update description');
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await axios.get(`${API}/admin/job-duties-summary`);
      setSummaryData(response.data);
      setShowSummaryModal(true);
    } catch (error) {
      toast.error('Failed to load summary');
    }
  };

  const fetchPendingReminders = async () => {
    try {
      const response = await axios.get(`${API}/admin/job-duties/pending-reminders`);
      setPendingReminders(response.data);
    } catch (error) {
      toast.error('Failed to load pending reminders');
    }
  };

  const openPendingModal = async () => {
    await fetchPendingReminders();
    setShowPendingModal(true);
  };

  const handleDeleteReminder = async (dutyId, reminderId) => {
    try {
      await axios.delete(`${API}/admin/job-duties/${dutyId}/reminders/${reminderId}`);
      toast.success('Reminder deleted');
      fetchDuties();
      fetchStats();
    } catch (error) {
      toast.error('Failed to delete reminder');
    }
  };

  const handleFileUpload = async (e, dutyId, type, reminderId = null) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!documentName.trim()) {
      toast.error('Please enter a document name');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      let url = `${API}/admin/job-duties/${dutyId}/documents?document_name=${encodeURIComponent(documentName)}`;
      if (type === 'reminder' && reminderId) {
        url = `${API}/admin/job-duties/${dutyId}/reminders/${reminderId}/documents?document_name=${encodeURIComponent(documentName)}`;
      }
      await axios.post(url, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Document uploaded');
      setDocumentName('');
      setUploadingFor(null);
      fetchDuties();
    } catch (error) {
      toast.error('Failed to upload document');
    }
  };

  const handleDownloadDocument = async (dutyId, docId, fileName) => {
    try {
      const response = await axios.get(
        `${API}/admin/job-duties/${dutyId}/documents/${docId}`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const handleDeleteDocument = async (dutyId, docId) => {
    try {
      await axios.delete(`${API}/admin/job-duties/${dutyId}/documents/${docId}`);
      toast.success('Document deleted');
      fetchDuties();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Urgent':
        return 'bg-red-100 text-red-800';
      case 'High':
        return 'bg-orange-100 text-orange-800';
      case 'Normal':
        return 'bg-blue-100 text-blue-800';
      case 'Low':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active':
        return 'bg-green-100 text-green-800';
      case 'Completed':
        return 'bg-blue-100 text-blue-800';
      case 'Archived':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {!embedded && (
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-4xl font-semibold text-foreground tracking-tight">
              Job Duties
            </h1>
            <p className="text-muted-foreground mt-2">
              Manage job duties and reminders for personnel
            </p>
          </div>
          <Button onClick={() => setShowAddModal(true)} data-testid="add-duty-btn">
            <Plus size={18} className="mr-2" />
            Add Job Duty
          </Button>
        </div>
      )}

      {embedded && (
        <div className="flex items-center justify-between">
          <p className="text-muted-foreground">Manage job duties and reminders for personnel</p>
          <Button onClick={() => setShowAddModal(true)} data-testid="add-duty-btn" size="sm">
            <Plus size={16} className="mr-1" />
            Add Job Duty
          </Button>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-3">
        <Button variant="outline" onClick={fetchSummary} data-testid="summary-btn">
          <ListTodo size={18} className="mr-2" />
          Job Duties Summary
        </Button>
        <Button variant="outline" onClick={openPendingModal} data-testid="pending-btn">
          <Bell size={18} className="mr-2" />
          Pending Job Duties
          {stats?.pending_reminders > 0 && (
            <Badge className="ml-2 bg-red-500 text-white">{stats.pending_reminders}</Badge>
          )}
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="p-4">
            <div className="text-2xl font-bold">{stats.total}</div>
            <div className="text-sm text-muted-foreground">Total</div>
          </Card>
          <Card className="p-4">
            <div className="text-2xl font-bold text-green-600">{stats.active}</div>
            <div className="text-sm text-muted-foreground">Active</div>
          </Card>
          <Card className="p-4">
            <div className="text-2xl font-bold text-blue-600">{stats.completed}</div>
            <div className="text-sm text-muted-foreground">Completed</div>
          </Card>
          <Card className="p-4">
            <div className="text-2xl font-bold text-orange-600">{stats.unassigned}</div>
            <div className="text-sm text-muted-foreground">Unassigned</div>
          </Card>
          <Card className="p-4">
            <div className="text-2xl font-bold text-purple-600">{stats.pending_reminders}</div>
            <div className="text-sm text-muted-foreground">Pending Reminders</div>
          </Card>
        </div>
      )}

      <Card className="p-6">
        {/* Filters */}
        <div className="flex gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={18} />
            <Input
              placeholder="Search job duties..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="pl-10"
            />
          </div>
          <select
            className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Statuses</option>
            <option value="Active">Active</option>
            <option value="Completed">Completed</option>
            <option value="Archived">Archived</option>
          </select>
        </div>

        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Loading...</div>
        ) : duties.length === 0 ? (
          <div className="text-center py-12">
            <AlertCircle className="mx-auto mb-4 text-muted-foreground" size={48} />
            <p className="text-muted-foreground">No job duties found</p>
          </div>
        ) : (
          <div className="space-y-4">
            {duties.map((duty) => (
              <Card
                key={duty.id}
                className={`border ${expandedDuty === duty.id ? 'border-primary' : ''}`}
              >
                <div
                  className="p-4 cursor-pointer"
                  onClick={() => setExpandedDuty(expandedDuty === duty.id ? null : duty.id)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-lg">{duty.title}</h3>
                        <Badge className={getStatusColor(duty.status)}>{duty.status}</Badge>
                        <Badge className={getPriorityColor(duty.priority)}>{duty.priority}</Badge>
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                        {duty.assigned_to_name && (
                          <span className="flex items-center gap-1">
                            <User size={14} />
                            {duty.assigned_to_name}
                          </span>
                        )}
                        {duty.due_date && (
                          <span className="flex items-center gap-1">
                            <Calendar size={14} />
                            Due: {formatDate(duty.due_date)}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Bell size={14} />
                          {duty.reminders?.length || 0} reminders
                        </span>
                        <span className="flex items-center gap-1">
                          <FileText size={14} />
                          {duty.documents?.length || 0} docs
                        </span>
                      </div>
                    </div>
                    {expandedDuty === duty.id ? (
                      <ChevronUp size={20} className="text-muted-foreground" />
                    ) : (
                      <ChevronDown size={20} className="text-muted-foreground" />
                    )}
                  </div>
                </div>

                {/* Expanded Content */}
                {expandedDuty === duty.id && (
                  <div className="border-t p-4 space-y-6">
                    {/* Description */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium">Description</h4>
                        {editingDescription === duty.id ? (
                          <div className="flex gap-2">
                            <Button size="sm" onClick={() => handleSaveDescription(duty.id)}>
                              <Save size={14} className="mr-1" />
                              Save
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => {
                              setEditingDescription(null);
                              setEditedDescription('');
                            }}>
                              Cancel
                            </Button>
                          </div>
                        ) : (
                          <Button size="sm" variant="ghost" onClick={() => {
                            setEditingDescription(duty.id);
                            setEditedDescription(duty.description || '');
                          }}>
                            <Edit2 size={14} className="mr-1" />
                            Edit
                          </Button>
                        )}
                      </div>
                      {editingDescription === duty.id ? (
                        <textarea
                          className="w-full min-h-[100px] rounded-md border border-input bg-background px-3 py-2 text-sm"
                          value={editedDescription}
                          onChange={(e) => setEditedDescription(e.target.value)}
                          placeholder="Enter description..."
                        />
                      ) : (
                        <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                          {duty.description || 'No description'}
                        </p>
                      )}
                    </div>

                    {/* Assignment */}
                    <div>
                      <h4 className="font-medium mb-2">Assignment</h4>
                      <div className="flex items-center gap-3">
                        <select
                          className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm flex-1 max-w-xs"
                          value={duty.assigned_to || ''}
                          onChange={(e) => handleUpdateDuty(duty.id, { assigned_to: e.target.value || null })}
                        >
                          <option value="">Unassigned</option>
                          {personnel.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.last_name}, {p.first_name}
                            </option>
                          ))}
                        </select>
                        <select
                          className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                          value={duty.status}
                          onChange={(e) => handleUpdateDuty(duty.id, { status: e.target.value })}
                        >
                          <option value="Active">Active</option>
                          <option value="Completed">Completed</option>
                          <option value="Archived">Archived</option>
                        </select>
                        <select
                          className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                          value={duty.priority}
                          onChange={(e) => handleUpdateDuty(duty.id, { priority: e.target.value })}
                        >
                          <option value="Low">Low</option>
                          <option value="Normal">Normal</option>
                          <option value="High">High</option>
                          <option value="Urgent">Urgent</option>
                        </select>
                      </div>
                    </div>

                    {/* Reminders */}
                    <div>
                      <h4 className="font-medium mb-2">Reminders</h4>
                      <div className="space-y-2 mb-4">
                        {duty.reminders && duty.reminders.length > 0 ? (
                          duty.reminders.map((reminder) => (
                            <div
                              key={reminder.id}
                              className={`p-3 rounded-lg ${reminder.completed ? 'bg-gray-100' : 'bg-muted/30'}`}
                            >
                              {editingReminder === reminder.id ? (
                                // Edit mode
                                <div className="space-y-3">
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                    <Input
                                      type="date"
                                      value={editedReminder.reminder_date || ''}
                                      onChange={(e) => setEditedReminder({...editedReminder, reminder_date: e.target.value})}
                                    />
                                    <Input
                                      type="time"
                                      value={editedReminder.reminder_time || ''}
                                      onChange={(e) => setEditedReminder({...editedReminder, reminder_time: e.target.value})}
                                    />
                                    <Input
                                      placeholder="Email subject"
                                      value={editedReminder.email_subject || ''}
                                      onChange={(e) => setEditedReminder({...editedReminder, email_subject: e.target.value})}
                                    />
                                  </div>
                                  <div className="flex items-center gap-4">
                                    <label className="flex items-center gap-2 text-sm">
                                      <input
                                        type="checkbox"
                                        checked={editedReminder.is_recurring || false}
                                        onChange={(e) => setEditedReminder({...editedReminder, is_recurring: e.target.checked})}
                                        className="rounded"
                                      />
                                      <Repeat size={14} />
                                      Recurring
                                    </label>
                                    {editedReminder.is_recurring && (
                                      <>
                                        <select
                                          className="flex h-9 rounded-md border border-input bg-background px-3 py-1 text-sm"
                                          value={editedReminder.recurrence_type || ''}
                                          onChange={(e) => setEditedReminder({...editedReminder, recurrence_type: e.target.value})}
                                        >
                                          <option value="">Select frequency</option>
                                          <option value="daily">Daily</option>
                                          <option value="weekly">Weekly</option>
                                          <option value="monthly">Monthly</option>
                                          <option value="custom">Custom</option>
                                        </select>
                                        {editedReminder.recurrence_type === 'custom' && (
                                          <div className="flex items-center gap-2">
                                            <span className="text-sm">Every</span>
                                            <Input
                                              type="number"
                                              min="1"
                                              className="w-16 h-9"
                                              value={editedReminder.recurrence_interval || 1}
                                              onChange={(e) => setEditedReminder({...editedReminder, recurrence_interval: parseInt(e.target.value) || 1})}
                                            />
                                            <select
                                              className="flex h-9 rounded-md border border-input bg-background px-3 py-1 text-sm"
                                              value={editedReminder.recurrence_unit || 'days'}
                                              onChange={(e) => setEditedReminder({...editedReminder, recurrence_unit: e.target.value})}
                                            >
                                              <option value="days">Days</option>
                                              <option value="weeks">Weeks</option>
                                              <option value="months">Months</option>
                                            </select>
                                          </div>
                                        )}
                                      </>
                                    )}
                                  </div>
                                  <div className="flex gap-2">
                                    <Button size="sm" onClick={() => handleUpdateReminder(duty.id, reminder.id)}>
                                      <Save size={14} className="mr-1" />
                                      Save
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={() => {
                                      setEditingReminder(null);
                                      setEditedReminder({});
                                    }}>
                                      Cancel
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                // View mode
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-4 flex-wrap">
                                    <div className="flex items-center gap-2">
                                      <Calendar size={14} />
                                      <span className="text-sm">{formatDate(reminder.reminder_date)}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Clock size={14} />
                                      <span className="text-sm">{reminder.reminder_time}</span>
                                    </div>
                                    {reminder.email_subject && (
                                      <span className="text-sm text-muted-foreground">
                                        Subject: {reminder.email_subject}
                                      </span>
                                    )}
                                    {reminder.is_recurring && (
                                      <Badge className="bg-purple-100 text-purple-800">
                                        <Repeat size={12} className="mr-1" />
                                        {reminder.recurrence_type === 'custom' 
                                          ? `Every ${reminder.recurrence_interval} ${reminder.recurrence_unit}`
                                          : reminder.recurrence_type}
                                      </Badge>
                                    )}
                                    {reminder.completed ? (
                                      <Badge className="bg-gray-200 text-gray-800">
                                        <CheckCircle size={12} className="mr-1" />
                                        Completed
                                      </Badge>
                                    ) : reminder.sent ? (
                                      <Badge className="bg-green-100 text-green-800">
                                        <CheckCircle size={12} className="mr-1" />
                                        Sent
                                      </Badge>
                                    ) : (
                                      <Badge className="bg-yellow-100 text-yellow-800">Pending</Badge>
                                    )}
                                    {reminder.documents && reminder.documents.length > 0 && (
                                      <span className="text-xs text-muted-foreground">
                                        ({reminder.documents.length} docs)
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-1">
                                    {!reminder.completed && (
                                      <>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => {
                                            setEditingReminder(reminder.id);
                                            setEditedReminder({
                                              reminder_date: reminder.reminder_date,
                                              reminder_time: reminder.reminder_time,
                                              email_subject: reminder.email_subject,
                                              email_body: reminder.email_body,
                                              is_recurring: reminder.is_recurring,
                                              recurrence_type: reminder.recurrence_type,
                                              recurrence_interval: reminder.recurrence_interval,
                                              recurrence_unit: reminder.recurrence_unit,
                                            });
                                          }}
                                        >
                                          <Edit2 size={14} />
                                        </Button>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="text-green-600"
                                          onClick={() => handleCompleteReminder(duty.id, reminder.id)}
                                          title="Mark Complete"
                                        >
                                          <CheckCircle size={14} />
                                        </Button>
                                      </>
                                    )}
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="text-destructive"
                                      onClick={() => handleDeleteReminder(duty.id, reminder.id)}
                                    >
                                      <Trash2 size={14} />
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))
                        ) : (
                          <p className="text-sm text-muted-foreground">No reminders set</p>
                        )}
                      </div>

                      {/* Add Reminder */}
                      <div className="space-y-3 p-3 border rounded-lg">
                        <h5 className="text-sm font-medium">Add New Reminder</h5>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                          <Input
                            type="date"
                            value={newReminder.reminder_date}
                            onChange={(e) => setNewReminder({ ...newReminder, reminder_date: e.target.value })}
                            placeholder="Date"
                          />
                          <Input
                            type="time"
                            value={newReminder.reminder_time}
                            onChange={(e) => setNewReminder({ ...newReminder, reminder_time: e.target.value })}
                          />
                          <Input
                            placeholder="Email subject"
                            value={newReminder.email_subject}
                            onChange={(e) => setNewReminder({ ...newReminder, email_subject: e.target.value })}
                          />
                          <Button onClick={() => handleAddReminder(duty.id)}>
                            <Plus size={14} className="mr-1" />
                            Add
                          </Button>
                        </div>
                        <div className="flex items-center gap-4">
                          <label className="flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              checked={newReminder.is_recurring}
                              onChange={(e) => setNewReminder({ ...newReminder, is_recurring: e.target.checked })}
                              className="rounded"
                            />
                            <Repeat size={14} />
                            Recurring
                          </label>
                          {newReminder.is_recurring && (
                            <>
                              <select
                                className="flex h-9 rounded-md border border-input bg-background px-3 py-1 text-sm"
                                value={newReminder.recurrence_type}
                                onChange={(e) => setNewReminder({ ...newReminder, recurrence_type: e.target.value })}
                              >
                                <option value="">Select frequency</option>
                                <option value="daily">Daily</option>
                                <option value="weekly">Weekly</option>
                                <option value="monthly">Monthly</option>
                                <option value="custom">Custom</option>
                              </select>
                              {newReminder.recurrence_type === 'custom' && (
                                <div className="flex items-center gap-2">
                                  <span className="text-sm">Every</span>
                                  <Input
                                    type="number"
                                    min="1"
                                    className="w-16 h-9"
                                    value={newReminder.recurrence_interval}
                                    onChange={(e) => setNewReminder({ ...newReminder, recurrence_interval: parseInt(e.target.value) || 1 })}
                                  />
                                  <select
                                    className="flex h-9 rounded-md border border-input bg-background px-3 py-1 text-sm"
                                    value={newReminder.recurrence_unit}
                                    onChange={(e) => setNewReminder({ ...newReminder, recurrence_unit: e.target.value })}
                                  >
                                    <option value="days">Days</option>
                                    <option value="weeks">Weeks</option>
                                    <option value="months">Months</option>
                                  </select>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Documents */}
                    <div>
                      <h4 className="font-medium mb-2">Documents</h4>
                      <div className="space-y-2 mb-4">
                        {duty.documents && duty.documents.length > 0 ? (
                          duty.documents.map((doc) => (
                            <div
                              key={doc.id}
                              className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                            >
                              <div className="flex items-center gap-3">
                                <FileText size={16} className="text-primary" />
                                <div>
                                  <p className="text-sm font-medium">{doc.document_name}</p>
                                  <p className="text-xs text-muted-foreground">{doc.file_name}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDownloadDocument(duty.id, doc.id, doc.file_name)}
                                >
                                  <Download size={14} />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-destructive"
                                  onClick={() => handleDeleteDocument(duty.id, doc.id)}
                                >
                                  <Trash2 size={14} />
                                </Button>
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="text-sm text-muted-foreground">No documents attached</p>
                        )}
                      </div>

                      {/* Upload Document */}
                      <div className="flex gap-2">
                        <Input
                          value={documentName}
                          onChange={(e) => setDocumentName(e.target.value)}
                          placeholder="Document name"
                          className="max-w-xs"
                        />
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={(e) => handleFileUpload(e, duty.id, 'duty')}
                          className="hidden"
                        />
                        <Button
                          variant="outline"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={!documentName.trim()}
                        >
                          <Upload size={14} className="mr-1" />
                          Upload
                        </Button>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end pt-4 border-t">
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDeleteDuty(duty.id)}
                      >
                        <Trash2 size={14} className="mr-1" />
                        Delete Job Duty
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </Card>

      {/* Add Job Duty Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-lg p-6 m-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Add Job Duty</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowAddModal(false)}>
                <X size={18} />
              </Button>
            </div>
            <form onSubmit={handleCreateDuty} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Title *</label>
                <Input
                  value={newDuty.title}
                  onChange={(e) => setNewDuty({ ...newDuty, title: e.target.value })}
                  placeholder="Job duty title"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Description</label>
                <textarea
                  className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={newDuty.description}
                  onChange={(e) => setNewDuty({ ...newDuty, description: e.target.value })}
                  placeholder="Describe the job duty..."
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Assign To</label>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={newDuty.assigned_to}
                    onChange={(e) => setNewDuty({ ...newDuty, assigned_to: e.target.value })}
                  >
                    <option value="">Select person...</option>
                    {personnel.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.last_name}, {p.first_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Priority</label>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={newDuty.priority}
                    onChange={(e) => setNewDuty({ ...newDuty, priority: e.target.value })}
                  >
                    <option value="Low">Low</option>
                    <option value="Normal">Normal</option>
                    <option value="High">High</option>
                    <option value="Urgent">Urgent</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Due Date</label>
                <Input
                  type="date"
                  value={newDuty.due_date}
                  onChange={(e) => setNewDuty({ ...newDuty, due_date: e.target.value })}
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowAddModal(false)}>
                  Cancel
                </Button>
                <Button type="submit">Create Job Duty</Button>
              </div>
            </form>
          </Card>
        </div>
      )}

      {/* Job Duties Summary Modal */}
      {showSummaryModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-3xl max-h-[80vh] m-4 flex flex-col">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-semibold">Job Duties Summary</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowSummaryModal(false)}>
                <X size={18} />
              </Button>
            </div>
            <div className="overflow-y-auto flex-1 p-6">
              {summaryData.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No assigned job duties found</p>
              ) : (
                <div className="space-y-4">
                  {summaryData.map((person) => (
                    <div key={person.personnel_id} className="border rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <User size={16} className="text-primary" />
                        <span className="font-semibold">{person.personnel_name}</span>
                        <Badge className="bg-muted">{person.duties.length} duties</Badge>
                      </div>
                      <div className="ml-6 space-y-1">
                        {person.duties.map((duty) => (
                          <div key={duty.id} className="flex items-center gap-3 text-sm">
                            <span className="text-muted-foreground">•</span>
                            <span>{duty.title}</span>
                            <Badge className={getPriorityColor(duty.priority)} variant="outline">
                              {duty.priority}
                            </Badge>
                            {duty.due_date && (
                              <span className="text-xs text-muted-foreground">
                                Due: {formatDate(duty.due_date)}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>
        </div>
      )}

      {/* Pending Job Duties Modal */}
      {showPendingModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-4xl max-h-[80vh] m-4 flex flex-col">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-semibold">Pending Job Duty Reminders</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowPendingModal(false)}>
                <X size={18} />
              </Button>
            </div>
            <div className="overflow-y-auto flex-1 p-6">
              {pendingReminders.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No pending reminders</p>
              ) : (
                <div className="space-y-3">
                  {pendingReminders.map((reminder) => (
                    <div
                      key={`${reminder.duty_id}-${reminder.reminder_id}`}
                      className={`border rounded-lg p-4 ${
                        reminder.is_overdue ? 'border-red-300 bg-red-50' : 
                        reminder.is_due_today ? 'border-yellow-300 bg-yellow-50' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-semibold">{reminder.duty_title}</h3>
                            {reminder.is_overdue && (
                              <Badge className="bg-red-100 text-red-800">
                                <AlertCircle size={12} className="mr-1" />
                                Overdue
                              </Badge>
                            )}
                            {reminder.is_due_today && !reminder.is_overdue && (
                              <Badge className="bg-yellow-100 text-yellow-800">Due Today</Badge>
                            )}
                            {reminder.is_recurring && (
                              <Badge className="bg-purple-100 text-purple-800">
                                <Repeat size={12} className="mr-1" />
                                {reminder.recurrence_type}
                              </Badge>
                            )}
                            {reminder.sent && (
                              <Badge className="bg-green-100 text-green-800">Sent</Badge>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Calendar size={14} />
                              {formatDate(reminder.reminder_date)} at {reminder.reminder_time}
                            </span>
                            <span className="flex items-center gap-1">
                              <User size={14} />
                              {reminder.assigned_to_name || 'Unassigned'}
                            </span>
                            {reminder.email_subject && (
                              <span>Subject: {reminder.email_subject}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleSendReminder(reminder.duty_id, reminder.reminder_id)}
                            disabled={!reminder.assigned_to_email}
                            title={reminder.assigned_to_email ? 'Send reminder email' : 'No email address'}
                          >
                            <Send size={14} className="mr-1" />
                            Send
                          </Button>
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => handleCompleteReminder(reminder.duty_id, reminder.reminder_id)}
                          >
                            <CheckCircle size={14} className="mr-1" />
                            Complete
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
